"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Check, ChevronLeft, ChevronRight, Loader, Lock, Paperclip, Quote, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  label: "Tu Evento"
}, {
  number: "02",
  label: "Detalles del Servicio"
}, {
  number: "03",
  label: "Selección de Fecha"
}, {
  number: "04",
  label: "Confirmación"
}];
const disciplines = ["Maquillaje de Novia", "Cabello y Maquillaje Editorial", "Estilismo para Eventos Especiales", "Sesión de Portafolio de Modelos", "Prueba de Novia", "Reserva de Grupo", "Otro"];
const timeSlots = ["09:00 – 10:00 AM", "10:30 – 11:30 AM", "01:00 – 02:00 PM", "02:30 – 03:30 PM", "04:00 – 05:00 PM"];
const nextSteps = [{
  num: "01",
  title: "Consulta Inicial",
  desc: "Reviso los detalles de tu evento dentro de 24 horas para confirmar disponibilidad."
}, {
  num: "02",
  title: "Consulta Personal",
  desc: "Una breve charla para discutir tu visión, preferencias de estilo y cronograma."
}, {
  num: "03",
  title: "Reserva Confirmada",
  desc: "Una vez finalizados los detalles, aseguro tu fecha con un acuerdo formal."
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = React.useState(0);
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = React.useState<string>("");
  const [confidential, setConfidential] = React.useState(false);
  const [fileName, setFileName] = React.useState<string>("");
  const goNext = () => setStep(s => Math.min(s + 1, 3));
  const goPrev = () => setStep(s => Math.max(s - 1, 0));
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <Check className="size-5 text-primary-foreground" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-primary font-mono">Solicitud Recibida</p>
        </div>
        <h3 className="font-default text-3xl md:text-4xl text-foreground mb-4 leading-tight">
          Tu solicitud de reserva<br /><em className="italic text-primary">ha sido recibida.</em>
        </h3>
        <p className="text-muted-foreground mb-8 leading-relaxed">{successMessage}</p>
        <Button onClick={() => {
        resetForm();
        setStep(0);
      }} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Enviar Otra Solicitud
        </Button>
      </div>;
  }
  return <div>
      {}
      <div className="mb-12">
        <div className="flex items-center justify-between relative">
          {steps.map((s, i) => <div key={s.number} data-index={i} className="flex flex-col items-center relative z-10 flex-1">
              <div className={`size-10 rounded-lg border flex items-center justify-center font-mono text-xs transition-all ${i < step ? "bg-primary border-primary text-primary-foreground" : i === step ? "bg-primary border-primary text-primary-foreground" : "border-border text-muted-foreground bg-transparent"}`}>
                {i < step ? <Check className="size-4" /> : s.number}
              </div>
              <p className={`mt-3 text-[10px] tracking-[0.2em] uppercase font-mono text-center ${i === step ? "text-primary" : "text-muted-foreground"}`}>
                {s.label}
              </p>
              {i < steps.length - 1 && <div className={`absolute top-5 left-[calc(50%+1.25rem)] right-[calc(-50%+1.25rem)] h-px ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>)}
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-10">
        {}
        {step === 0 && <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              <FormField control={control} name="full_name" rules={{
            required: "El nombre completo es obligatorio"
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">Nombre Completo</FormLabel>
                    <FormControl>
                      <Input placeholder="Tu Nombre" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email_address" rules={{
            required: "El correo es obligatorio",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Correo inválido"
            }
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">Correo Electrónico</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="correo@ejemplo.com" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone_number" rules={{
            required: "El número de teléfono es obligatorio"
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">Número de Teléfono</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+34 000 000 000" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="company_name" render={({
            field
          }) => <FormItem>
                    <FormLabel className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">Ubicación del Evento</FormLabel>
                    <FormControl>
                      <Input placeholder="Valencia / Madrid" {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={control} name="field_of_invention" render={({
          field
        }) => <FormItem>
                  <FormLabel className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">Tipo de Servicio</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary h-11 w-full text-base">
                        <SelectValue placeholder="Selecciona un servicio" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-border rounded-lg">
                        {disciplines.map((d, i) => <SelectItem key={d} data-index={i} value={d}>
                            {d}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>}

        {}
        {step === 1 && <div className="space-y-10">
            <FormField control={control} name="invention_summary" rules={{
          required: "Por favor describe tu evento",
          maxLength: {
            value: 500,
            message: "Máximo 500 caracteres"
          }
        }} render={({
          field
        }) => <FormItem>
                  <FormLabel className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">Detalles y Visión del Evento</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Cuéntame sobre tu evento y el look que deseas..." maxLength={500} {...field} className="border-border rounded-lg bg-transparent min-h-[200px] focus-visible:ring-0 focus-visible:border-primary font-default text-base leading-loose" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground mb-3">Inspiración (Opcional)</p>
              <label className="border border-dashed border-border rounded-lg p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary transition-colors">
                <div className="flex items-center gap-3">
                  <Paperclip className="size-5 text-primary" />
                  <Upload className="size-5 text-primary" />
                </div>
                <p className="text-sm text-foreground">
                  {fileName || "Adjunta mood boards o fotos de referencia"}
                </p>
                <p className="text-xs text-muted-foreground">PDF, PNG, JPG — hasta 25MB</p>
                <input type="file" className="hidden" onChange={e => setFileName(e.target.files?.[0]?.name || "")} />
              </label>
            </div>

            <div className="flex items-start gap-3 border border-border rounded-lg p-5 bg-card">
              <Checkbox id="confidential" checked={confidential} onCheckedChange={c => setConfidential(c === true)} className="mt-0.5 border-primary data-[state=checked]:bg-primary" />
              <label htmlFor="confidential" className="text-sm text-foreground leading-relaxed cursor-pointer">
                Acepto los términos de reserva y confirmo que toda la información proporcionada es precisa para mi evento.
              </label>
            </div>
          </div>}

        {}
        {step === 2 && <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-10">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground mb-4">Selecciona una Fecha</p>
              <div className="border border-border rounded-lg p-2 bg-card inline-block">
                <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} /></SafeHydrate>
              </div>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground mb-4">Hora Preferida</p>
              <div className="space-y-2">
                {timeSlots.map((slot, i) => <Button key={slot} data-index={i} type="button" onClick={() => setSelectedTime(slot)} variant="ghost" className={`w-full justify-start px-4 py-3 rounded-lg border font-mono text-xs tracking-wider transition-all ${selectedTime === slot ? "border-primary bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground" : "border-border text-foreground hover:border-primary hover:text-primary"}`}>
                    {slot}
                  </Button>)}
              </div>
            </div>
          </div>}

        {}
        {step === 3 && <div className="space-y-6">
            <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-primary mb-2">Resumen de la Reserva</p>
            <h3 className="font-default text-3xl text-foreground mb-8">Por favor revisa<br /><em className="italic text-primary">tus datos.</em></h3>
            <div className="border-t border-border">
              {[{
            label: "Servicio",
            value: "Seleccionado en el Paso 01"
          }, {
            label: "Fecha",
            value: selectedDate ? selectedDate.toLocaleDateString("es-ES", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric"
            }) : "No seleccionada"
          }, {
            label: "Hora",
            value: selectedTime || "No seleccionada"
          }, {
            label: "Términos",
            value: confidential ? "Aceptado" : "Pendiente"
          }].map((row, i) => <div key={row.label} data-index={i} className="grid grid-cols-[180px_1fr] gap-6 py-4 border-b border-border">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">{row.label}</p>
                  <p className="text-foreground font-default">{row.value}</p>
                </div>)}
            </div>
            {submitError && <div className="p-4 border border-destructive rounded-lg text-destructive text-sm">{submitError}</div>}
          </div>}

        {}
        <div className="flex items-center justify-between pt-8 border-t border-border">
          <Button type="button" onClick={goPrev} disabled={step === 0} variant="ghost" className="inline-flex items-center gap-2 text-sm tracking-wider text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-mono px-0">
            <ChevronLeft className="size-4" />
            Anterior
          </Button>

          {step < 3 ? <Button type="button" onClick={goNext} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 h-11">
              Continuar
              <ChevronRight className="size-4" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-8 h-11">
              {isSubmitting ? <>
                  <Loader className="size-4 animate-spin" />
                  Enviando...
                </> : <>
                  Enviar Solicitud
                  <ChevronRight className="size-4" />
                </>}
            </Button>}
        </div>
      </Form>
    </div>;
}
export default function BridalSpecialEvents() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4366" id="bridal-special-events" className="dark bg-background text-foreground pt-[120px] pb-24 px-6 md:px-10 lg:px-16">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="mb-16 max-w-4xl">
          <p className="text-[11px] tracking-[0.35em] uppercase text-primary font-mono mb-8">
            Consulta de Reserva — Paso 01 de 04
          </p>
          <h2 className="font-default text-5xl md:text-6xl lg:text-[56px] leading-[1.05] text-foreground">
            Asegura Tu<br />
            <em className="italic text-primary">Sesión de Belleza.</em>
          </h2>
          <p className="mt-8 text-muted-foreground font-default text-lg leading-relaxed max-w-2xl">
            Servicios profesionales de cabello y maquillaje para modelos, novias y eventos especiales en Valencia y Madrid. Solo con cita previa.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-16 lg:gap-20">
          {}
          <div>
            <FormProvider formKey="event_enquiry_form_services" formId="event_enquiry_form_services" sectionName="bridal-special-events" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            company_name: "",
            field_of_invention: "",
            invention_summary: "",
            preferred_date: "",
            preferred_time: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true
            },
            invention_summary: {
              required: true,
              maxLength: 500
            },
            preferred_date: {
              required: true
            }
          }} submitText="Enviar Solicitud" successMessage="Tu solicitud de reserva ha sido recibida. Responderé dentro de un día hábil para confirmar los detalles." errorMessage="No pudimos enviar tu solicitud. Por favor, inténtalo de nuevo o contáctame vía Instagram.">
              <ConsultationForm />
            </FormProvider>
          </div>

          {}
          <aside className="space-y-8">
            {}
            <div className="border border-border p-8 bg-card">
              <p className="text-[10px] tracking-[0.3em] uppercase font-mono text-primary mb-5">Qué Sucede Después</p>
              <h3 className="font-default text-2xl text-card-foreground mb-6 leading-snug">
                Un proceso<br /><em className="italic text-primary">simple, elegante.</em>
              </h3>
              <div className="space-y-5">
                {nextSteps.map((item, i) => <div key={item.num} data-index={i} className="flex gap-4">
                    <span className="font-default text-2xl italic text-primary leading-none mt-0.5">{item.num}</span>
                    <div>
                      <p className="font-default text-base text-card-foreground mb-1">{item.title}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            {}
            <div className="border border-border p-8 bg-card">
              <div className="flex items-center gap-3 mb-5">
                <Lock className="size-4 text-primary" />
                <p className="text-[10px] tracking-[0.3em] uppercase font-mono text-primary">Mi Compromiso</p>
              </div>
              <h3 className="font-default text-2xl text-card-foreground mb-4 leading-snug">
                El profesionalismo es<br /><em className="italic text-primary">mi base.</em>
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Cada reserva se maneja con cuidado. Tu visión y los detalles del evento son tratados con el máximo respeto para asegurar una experiencia impecable.
              </p>
            </div>

            {}
            <div className="border border-border p-8 bg-card relative">
              <Quote className="size-7 text-primary mb-4" />
              <p className="font-default italic text-xl text-card-foreground leading-snug mb-6">"La mejor! Muchas gracias reina. Te quiero."</p>
              <div className="border-t border-border pt-4">
                <p className="text-sm text-card-foreground font-default">Carmel Musica</p>
                <p className="text-xs text-muted-foreground tracking-wider mt-1 font-mono">Professional Model</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}